import { observer, useLocalObservable } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type {
  ICampaignAttributes,
  IConversionFilters as IFilters,
  IEvent,
  IFlight,
  TAttributionModel,
} from '@feathr/blackbox';
import { Campaign, Event, Flight } from '@feathr/blackbox';
import { AsyncSelect } from '@feathr/components';
import type { DisplayModel } from '@feathr/rachis';
import { useStore } from '@feathr/report_components/state';

import * as styles from './ConversionTableCategorySelect.css';

interface IOption {
  value: string;
  label: string;
}

interface IProps {
  attributionModel: TAttributionModel;
  label?: string;
  onSelectCategory: (categories: IOption[]) => void;
  values: string[] | undefined;
  filters: IFilters;
  model: DisplayModel<ICampaignAttributes> | DisplayModel<IEvent> | DisplayModel<IFlight>;
}

function ConversionTableCategorySelect({
  attributionModel,
  label = 'Filter by',
  onSelectCategory,
  values,
  filters,
  model,
}: Readonly<IProps>): JSX.Element {
  const { Events, Flights, Campaigns } = useStore();
  const state = useLocalObservable(() => ({
    options: [] as IOption[],
    setOptions: (options: IOption[]): void => {
      state.options = options;
    },
  }));

  async function loadCategoryOptions(): Promise<IOption[]> {
    const collections = [
      { collection: Events, check: (m) => m instanceof Event },
      { collection: Flights, check: (m) => m instanceof Flight },
      { collection: Campaigns, check: (m) => m instanceof Campaign },
    ];

    const matchingCollection = collections.find(({ check }) => check(model))!.collection;
    // eslint-disable-next-line
    const { category__in, ...rest } = filters;
    const categories = await matchingCollection.getConversionCategories(model.id, {
      ...rest,
      attribution_model: attributionModel,
    });
    state.setOptions(categories.map((cat: string) => ({ label: cat, value: cat })));
    return state.options;
  }

  function getOptionLabel(option: IOption): string {
    return option.label;
  }

  function getOptionValue(option: IOption): string {
    return option.value;
  }

  function filterOption({ label }: IOption, inputValue: string): boolean {
    return label.toLowerCase().includes(inputValue.toLowerCase());
  }

  return (
    <AsyncSelect<IOption>
      defaultOptions={true}
      filterOption={filterOption}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      isClearable={true}
      isMulti={true}
      key={attributionModel}
      label={label}
      loadOptions={loadCategoryOptions}
      name={'category'}
      onSelectMulti={onSelectCategory}
      placeholder={'Category'}
      value={values ? state.options.filter((option) => values.includes(option.value)) : undefined}
      wrapperClassName={styles.select}
    />
  );
}

export default observer(ConversionTableCategorySelect);
